import { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../utils/api';

const normalizeDocumentTypes = (data = [], { activeOnly = true } = {}) => {
  if (!Array.isArray(data)) {
    return [];
  }

  const mapped = data
    .map((item) => {
      const isActive = item.isActive ?? item.is_active ?? true;
      return {
        id: item.id ?? item.documentTypeId ?? item.document_type_id ?? null,
        name: item.name || item.documentName || item.document_name || '',
        isMandatory: item.isMandatory ?? item.is_mandatory ?? false,
        isActive,
        isSystem: item.isSystem ?? item.is_system ?? false,
        displayOrder: typeof item.displayOrder === 'number' ? item.displayOrder : null,
      };
    })
    .filter((item) => item.id && item.name);

  const filtered = activeOnly ? mapped.filter((item) => item.isActive) : mapped;

  return filtered.sort((first, second) => {
    if (first.isSystem && !second.isSystem) return -1;
    if (!first.isSystem && second.isSystem) return 1;
    if (first.displayOrder !== null && second.displayOrder !== null) {
      return first.displayOrder - second.displayOrder;
    }
    if (first.displayOrder !== null) return -1;
    if (second.displayOrder !== null) return 1;
    return first.name.localeCompare(second.name);
  });
};

const useDocumentTypes = ({ activeOnly = true } = {}) => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const fetchDocumentTypes = async () => {
      setIsLoading(true);
      setError('');

      const requests = [
        { path: '/api/public/document-types', options: { skipAuthRedirect: true } },
        { path: '/api/admin/settings/documents', options: {} },
      ];

      for (const request of requests) {
        try {
          const response = await apiRequest(request.path, request.options);
          if (!isMounted) {
            return;
          }
          setDocumentTypes(normalizeDocumentTypes(response.data, { activeOnly }));
          setError('');
          setIsLoading(false);
          return;
        } catch (requestError) {
          if (request === requests[requests.length - 1]) {
            if (!isMounted) {
              return;
            }
            setDocumentTypes([]);
            setError(requestError.message || 'Unable to load document types.');
          }
        }
      }

      setIsLoading(false);
    };

    fetchDocumentTypes();

    return () => {
      isMounted = false;
    };
  }, [activeOnly]);

  const mandatoryDocumentTypes = useMemo(
    () => documentTypes.filter((type) => type.isMandatory),
    [documentTypes],
  );

  return {
    documentTypes,
    mandatoryDocumentTypes,
    isLoading,
    error,
  };
};

export default useDocumentTypes;
